import { useEffect, useState } from "react";
import { Download, Lock } from "lucide-react";
import { errorMessage, get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PdfViewer } from "@/features/library/PdfViewer";
import { bytes } from "@/features/library/format";
import { downloadFile, fileUrl, LOCKED_FILE_MESSAGE } from "./download";
import { FileName, fileTypeLabel, useFileMeta } from "./FileName";
import { fetchDecryptedFile } from "@/features/crypto/ops";
import type { FileRow } from "./types";

type PreviewFile = Pick<FileRow, "id" | "filename" | "mime" | "sizeBytes" | "sha256" | "encrypted"> & { encMeta?: string | null };

export function isTextLike(f: Pick<FileRow, "filename" | "mime">) {
  return /^text\/|json|csv|yaml|xml/.test(f.mime) || /\.(py|r|sh|js|ts|md|txt|csv|tsv|ipynb|json|ya?ml|toml|ini|cfg|log|tex|bib)$/i.test(f.filename);
}

export function canPreview(f: PreviewFile) {
  if (f.encrypted) return !!f.encMeta;
  return f.mime === "application/pdf" || /^image\/(png|jpeg|gif|webp)$/.test(f.mime) || isTextLike(f);
}

function TextPreview({ file }: { file: PreviewFile }) {
  const [state, setState] = useState<{ text?: string; truncated?: boolean; note?: string; error?: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    get<{ text: string; truncated: boolean; note: string }>(`/api/attachments/${file.id}/text-preview`)
      .then((r) => !cancelled && setState(r))
      .catch((e) => !cancelled && setState({ error: errorMessage(e) }));
    return () => {
      cancelled = true;
    };
  }, [file.id]);
  if (!state) return <p className="text-sm text-muted-foreground">Loading preview…</p>;
  if (state.error) return <p className="text-sm text-destructive">{state.error}</p>;
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{state.note}</p>
      <pre className="max-h-[60vh] overflow-auto rounded bg-muted p-3 text-xs">{state.text}</pre>
      {state.truncated && <p className="text-xs text-muted-foreground">Preview truncated. Download the file to see all of it.</p>}
    </div>
  );
}

export function FilePreviewDialog({
  file,
  onClose,
  title,
  actions,
  wide,
  initialPage,
}: {
  file: PreviewFile;
  onClose(): void;
  title?: string;
  actions?: React.ReactNode;
  /** Larger window, for reading a paper. */
  wide?: boolean;
  /** Open the PDF at this page. */
  initialPage?: number;
}) {
  const [page, setPage] = useState(initialPage ?? 1);
  const url = fileUrl(file);
  let body: React.ReactNode;
  if (file.encrypted) {
    body = <EncryptedPreview file={file} page={page} setPage={setPage} />;
  } else if (!url) {
    body = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Lock className="size-4" aria-hidden /> {LOCKED_FILE_MESSAGE}
      </p>
    );
  } else if (file.mime === "application/pdf") {
    body = <PdfViewer attachmentId={file.id} page={page} onPageChange={setPage} labels={{}} />;
  } else if (/^image\/(png|jpeg|gif|webp)$/.test(file.mime)) {
    body = <img src={url} alt={file.filename} className="mx-auto max-h-[65vh] max-w-full rounded border object-contain" />;
  } else if (isTextLike(file)) {
    body = <TextPreview file={file} />;
  } else {
    body = <p className="text-sm text-muted-foreground">No preview for this file type. Download it to open it with another program.</p>;
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={cn("max-h-[90vh] overflow-y-auto sm:max-w-3xl", wide && "sm:max-w-5xl")}>
        <DialogHeader>
          <DialogTitle className="min-w-0 pr-6">{title ?? <FileName file={file} />}</DialogTitle>
          <DialogDescription>
            {bytes(file.sizeBytes)} · {file.encrypted ? "end-to-end encrypted" : fileTypeLabel(file) === "Other" ? "File" : fileTypeLabel(file)}
          </DialogDescription>
        </DialogHeader>
        {body}
        <div className="flex flex-wrap justify-end gap-2">
          {actions}
          <Button size="sm" variant="outline" onClick={() => downloadFile(file)}>
            <Download /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Preview of an end-to-end encrypted file: decrypted in this tab only, never sent anywhere. */
function EncryptedPreview({ file, page, setPage }: { file: PreviewFile; page: number; setPage(p: number): void }) {
  const meta = useFileMeta(file);
  const [state, setState] = useState<{ url?: string; text?: string; error?: string } | null>(null);
  const kind = !meta ? null : meta.mime === "application/pdf" ? "pdf" : /^image\/(png|jpeg|gif|webp)$/.test(meta.mime) ? "image" : isTextLike(meta) ? "text" : "other";
  useEffect(() => {
    if (!meta || (kind !== "image" && kind !== "text")) return;
    let url: string | null = null;
    let alive = true;
    fetchDecryptedFile({ id: file.id, encMeta: file.encMeta ?? null })
      .then(async (f) => {
        if (!alive) return;
        if (kind === "image") {
          url = URL.createObjectURL(f.blob);
          setState({ url });
        } else setState({ text: new TextDecoder().decode(f.bytes.subarray(0, 200_000)) });
      })
      .catch((e) => alive && setState({ error: errorMessage(e) }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.id, file.encMeta, kind, meta]);
  if (!meta)
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Lock className="size-4" aria-hidden /> {LOCKED_FILE_MESSAGE}
      </p>
    );
  if (kind === "pdf") return <PdfViewer attachmentId={file.id} encMeta={file.encMeta} page={page} onPageChange={setPage} labels={{}} />;
  if (kind === "other") return <p className="text-sm text-muted-foreground">No preview for this file type. Download it to decrypt and open it.</p>;
  if (!state) return <p className="text-sm text-muted-foreground">Decrypting…</p>;
  if (state.error) return <p className="text-sm text-destructive">{state.error}</p>;
  if (state.url) return <img src={state.url} alt={meta.filename} className="mx-auto max-h-[65vh] max-w-full rounded border object-contain" />;
  return <pre className="max-h-[60vh] overflow-auto rounded bg-muted p-3 text-xs">{state.text}</pre>;
}
